
import {Component, Mixins, Prop, Ref, Watch} from "vue-property-decorator";
import FormWrapper from "@/components/FormWrapper.vue";
import FormDialog from "@/components/dialogs/FormDialog.vue";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import {PersonelPermissionEntity} from "@/entity/PersonelPermissionEntity";
import ObjectInputMixin from "@/mixins/ObjectInputMixin.vue";
import {StatusCodeHelper} from "@/helpers/StatusCodeHelper";
import {BildirimEntity} from "@/entity/BildirimEntity";
import app from "@/main";

@Component({
  components: {DeleteDialog, FormDialog, FormWrapper}
})
export default class KullaniciIzinleriForm extends Mixins(ObjectInputMixin) {
  @Prop({ type: Boolean, default: false }) isVisible!: boolean;
  @Ref('formWrapper') formWrapper!: FormWrapper;

  ready = false;
  items: Array<PersonelPermissionEntity> = [];
  allData: Array<PersonelPermissionEntity> = [];
  selectedCheckbox = false;
  StatusCodeHelper = new StatusCodeHelper();
  isimDegistir = true;
  openNodes: Array<string> = [];
  loadedNodes: Set<string> = new Set();
  treeviewKey = 0;
  previousPermissions: string[] = [];
  isProcessing = false;
  isLoadingChildren = false;
  // İzinler kod tarafından güncellenirken API çağrısını engelle (Reaktivite senkronizasyonu)
  isUpdatingPermissions = false;
  // Yeni Form Açılışı sırasında hatalı tetiklenmeleri engelle
  isFormInitializing = true;

  get rootDirs(): Array<PersonelPermissionEntity> {
    return this.items.filter((value) => value.parent === null)
  }

  /** Kisitli donemde izin agaci salt-okunur olur (madde 1). */
  get izinlerKisitli(): boolean {
    return this.$helper.lisansIslemKisitli('kullanici-izinleri');
  }

  personelTuruIsimDegistir() {
    this.isimDegistir = !this.isimDegistir
  }

  @Watch('isVisible')
  async onVisibilityChange(newVal: boolean, oldVal: boolean) {
    // Dialog açıldığında izinleri yeniden yükle
    if (newVal && !oldVal && !this.ready) {
      await this.load();
    }
  }

  mounted() {
    this.ready = false;
    this.loadedNodes.clear();
    this.openNodes = [];
    this.previousPermissions = [];
    this.isProcessing = false;
    this.isLoadingChildren = false;
    this.isUpdatingPermissions = false;
    this.isFormInitializing = true; // Mount anında aktif et
    this.treeviewKey++;
    this.load();
  }

  onClose() {
    // Önce treeview'ı gizle
    this.ready = false;


    // State'leri resetle
    this.selectedCheckbox = false;
    this.openNodes = [];
    this.loadedNodes.clear();
    this.previousPermissions = [];
    this.isProcessing = false;
    this.isLoadingChildren = false;
    this.isUpdatingPermissions = false;
    this.isFormInitializing = true;

    // Tüm yüklenmiş children'ları temizle
    this.allData.forEach(item => {
      this.$delete(item, 'children');
    });

    // Root item'ların children'ını boş array olarak tekrar ayarla (lazy loading için)
    this.items.forEach(item => {
      const hasChildren = this.allData.some(d => d.parent?.id === item.id);
      if (hasChildren) {
        this.$set(item, 'children', []);
      }
    });

    // Treeview'i reset et
    this.treeviewKey++;

    this.$emit("onClose");
  }

  // Lazy loading fonksiyonu
  async loadChildren(item: PersonelPermissionEntity) {
    if (this.loadedNodes.has(item.name)) {
      return;
    }

    this.isLoadingChildren = true;

    try {
      const children = this.allData.filter(
          (data) => data.parent?.id === item.id
      );

      children.forEach(child => {
        const childHasChildren = this.allData.some(data => data.parent?.id === child.id);
        if (childHasChildren) {
          child.children = [];
        } else {
          child.children = undefined;
        }
      });

      this.$set(item, 'children', children);

      if (this.localValue.id) {
        const response = await this.$http.get(
            `/api/v1/roles/${this.localValue.id}/permissions?filter=${item.id}`
        );

        const rolePermissionsData = response.data.data || response.data;
        const rolePermissionNames = rolePermissionsData.map((p: any) => p.name);

        const childrenNames: string[] = [];
        const addAllChildren = (parent: PersonelPermissionEntity) => {
          const subChildren = this.allData.filter(data => data.parent?.id === parent.id);
          subChildren.forEach(subChild => {
            childrenNames.push(subChild.name);
            addAllChildren(subChild);
          });
        };
        children.forEach((child: PersonelPermissionEntity) => {
          childrenNames.push(child.name);
          addAllChildren(child);
        });

        let updatedPermissions = this.localValue.permissions.filter(
            (perm: string) => !childrenNames.includes(perm)
        );

        const parentCurrentlySelected = this.localValue.permissions.includes(item.name);

        const isRootLevel = item.parent === null;
        if (isRootLevel && parentCurrentlySelected) {
          if (!updatedPermissions.includes(item.name)) {
            updatedPermissions.push(item.name);
          }
        }

        rolePermissionNames.forEach((permName: string) => {
          if (!updatedPermissions.includes(permName)) {
            updatedPermissions.push(permName);
          }
        });

        this.localValue.permissions = updatedPermissions;

        this.previousPermissions = [...updatedPermissions];

        if (!this.openNodes.includes(item.name)) {
          this.openNodes.push(item.name);
        }

        this.treeviewKey++;

        await this.$nextTick();
      }

      if(!this.localValue.id && this.localValue.permissions.includes(item.name)) {
        const updatedPermissions = [...this.localValue.permissions];
        children.forEach((child: PersonelPermissionEntity) => {
          if (!updatedPermissions.includes(child.name)) {
            updatedPermissions.push(child.name);
          }
        });

        this.localValue.permissions = updatedPermissions;
        this.previousPermissions = [...updatedPermissions];
        this.treeviewKey++;

        await this.$nextTick();
      }

      this.loadedNodes.add(item.name);

    } catch (error) {
      console.error('Children yüklenirken hata:', error);
      this.$set(item, 'children', []);
    } finally {
      this.isLoadingChildren = false;
    }
  }

  // Node açıldığında veya kapandığında tetiklenen event
  async onNodeOpen(openNodes: string[]) {
    const newlyOpenedNodes = openNodes.filter(nodeName => !this.openNodes.includes(nodeName));
    const closedNodes = this.openNodes.filter(nodeName => !openNodes.includes(nodeName));

    this.openNodes = openNodes;

    // Kapatılan node'ları cache'den çıkar ki tekrar açıldığında filter isteği atılsın
    closedNodes.forEach(nodeName => {
      this.loadedNodes.delete(nodeName);
    });

    // Kapatma işlemi (Sadece UI değişikliği)
    if (newlyOpenedNodes.length === 0 && closedNodes.length > 0) {

      this.isUpdatingPermissions = true;

      // Kapatma sırasında v-model anlık değişebilir, nextTick ile stabilizasyonu bekle.
      await this.$nextTick();

      this.isUpdatingPermissions = false;
      return;
    }

    // Açma işlemi (Lazy Load gerektirir)
    if (newlyOpenedNodes.length > 0) {

      // Açma işlemi başlarken kilit
      this.isUpdatingPermissions = true;

      for (const nodeName of newlyOpenedNodes) {
        const node = this.findNodeByName(nodeName);
        if (node && !this.loadedNodes.has(nodeName)) {
          await this.loadChildren(node);
        }
      }

      // Lazy load bittikten sonra Vue'nun tamamen stabilize olmasını bekle
      await this.$nextTick();

      // Kiliti kapat
      this.isUpdatingPermissions = false;
      return;
    }

    // Açma/Kapatma dışındaki diğer olaylarda isUpdatingPermissions tekrar false kalır.
  }

  // İsme göre node bul
  findNodeByName(name: string): PersonelPermissionEntity | undefined {
    return this.allData.find(item => item.name === name);
  }

  async load() {
    await this.getPermissions();
    if (this.localValue.id) {
      this.isUpdatingPermissions = true;
      await this.loadRolePermissions();
      this.isUpdatingPermissions = false;
    }
    this.ready = true;

    this.$nextTick(() => {
      if (this.formWrapper) {
        this.formWrapper.setDefaultLocalValue();
      }
      // KRİTİK: Form tamamen yüklendikten ve DOM stabilize olduktan sonra kilidi kaldır.
      setTimeout(() => {
        this.isFormInitializing = false;
      }, 100); // Küçük bir gecikme eklemek, Vue reaktivitesinin tamamen yatışmasını sağlar.
    });
  }

  async getPermissions() {
    this.allData = [];
    this.items = [];

    let response = await this.$helper.requestWithCache("/api/v1/permissions");
    let permissions = response.data;

    permissions.forEach((permission: any) => {
      let data: PersonelPermissionEntity = new PersonelPermissionEntity();
      data.id = permission.id;
      data.name = permission.name;
      data.label = permission.label;
      data.parent = null;

      this.allData.push(data);

      if (permission.parent_id) {
        this.allData.forEach((val) => {
          if (permission.parent_id === val.id) {
            data.parent = val;
          }
        });
      }
    });

    this.items = this.allData.filter(item => item.parent === null);
    this.items.forEach(parentItem => {
      const hasChildren = this.allData.some(item => item.parent?.id === parentItem.id);
      if (hasChildren) {
        parentItem.children = [];
      } else {
        parentItem.children = undefined;
      }
    });
  }

  async loadRolePermissions() {
    let response = await this.$http.get("/api/v1/roles/" + this.localValue.id + "/permissions");
    let permission: Array<string> = [];

    response.data.forEach((item: any) => {
      const permissionData = this.allData.find(p => p.name === item.name);
      if (permissionData && !permissionData.parent) {
        permission.push(item.name);
      }
    });

    this.localValue.permissions = permission;
    this.previousPermissions = [...permission];
  }

  protected async onValueChange() {
    if (this.value && this.value.id && this.ready) {
      this.ready = false;
      this.loadedNodes.clear();
      this.openNodes = [];
      this.previousPermissions = [];
      this.isProcessing = false;
      this.isLoadingChildren = false;
      this.isUpdatingPermissions = false;
      this.isFormInitializing = true; // Yeniden yükleme başlatıldığında
      this.treeviewKey++;
      await this.load();
    }

    if (this.localValue.permissions) {
      this.localValue.permissions = this.localValue.permissions.map((value: any) => {
        return typeof value === 'string' ? value : value.name;
      });
      // previousPermissions'ı senkronize et.
      this.previousPermissions = [...this.localValue.permissions];
    }
  }

  async refreshPermissionsForParent(permissionId: number) {

    this.isUpdatingPermissions = true;

    try {
      const response = await this.$http.get(
          `/api/v1/roles/${this.localValue.id}/permissions?filter=${permissionId}`
      );

      const backendPermissions = response.data.data || response.data;
      const backendPermissionNames = backendPermissions.map((p: any) => p.name);

      const childrenNames: string[] = [];
      const findAllChildren = (parentId: number) => {
        const children = this.allData.filter(p => p.parent?.id === parentId);
        children.forEach(child => {
          childrenNames.push(child.name);
          findAllChildren(child.id);
        });
      };
      findAllChildren(permissionId);

      let updatedPermissions = this.localValue.permissions.filter(
          (p: string) => !childrenNames.includes(p)
      );

      backendPermissionNames.forEach((permName: string) => {
        if (!updatedPermissions.includes(permName)) {
          updatedPermissions.push(permName);
        }
      });

      this.$set(this.localValue, 'permissions', updatedPermissions);
      this.previousPermissions = [...updatedPermissions];

      this.treeviewKey++;

      await this.$nextTick();
    } catch (error) {
      console.error(`Parent permissions güncellenirken hata (${permissionId}):`, error);
    } finally {
      this.isUpdatingPermissions = false;
    }
  }

  async onPermissionChange() {
    // !!! ENGELEYİCİ KONTROLLER !!!

    // 1. Reaktivite Kilitleri: Kod tarafından yapılan güncellemeleri engeller (load, açma/kapama)
    if (this.isUpdatingPermissions) {
      return;
    }

    // 2. Form Açılış Kilidi: Form yüklendiğinde/açıldığında oluşan hatalı ilk tetiklenmeleri engeller
    if (this.isFormInitializing) {
      return;
    }

    if (!this.localValue.id) {
      return;
    }

    if (this.isProcessing) {
      this.$toast.warning('Lütfen mevcut işlemin bitmesini bekleyin', {
        timeout: 2000
      });
      return;
    }

    if (this.isLoadingChildren) {
      return;
    }

    this.isProcessing = true;

    this.$toast.info('İzinler güncelleniyor...', {
      timeout: false,
      id: 'permission-update'
    });

    try {
      const currentPermissions = this.localValue.permissions || [];
      const previousPermissions = this.previousPermissions || [];

      const addedPermissions = currentPermissions.filter(
          (perm: string) => !previousPermissions.includes(perm)
      );

      const removedPermissions = previousPermissions.filter(
          (perm: string) => !currentPermissions.includes(perm)
      );

      // Değişiklik yoksa API çağrısı yapma
      if (addedPermissions.length === 0 && removedPermissions.length === 0) {
        this.$toast.dismiss('permission-update');
        this.isProcessing = false;
        return;
      }

      // --- Sadece bu noktadan sonra gerçekten bir API işlemi başlar (Attach/Detach) ---
      let hasChanges = false;

      // ... (Attach/Detach mantığı) ...

      // Eklenen her yetki için attach isteği at
      for (const permissionName of addedPermissions) {
        const permission = this.allData.find(p => p.name === permissionName);
        if (permission) {
          try {
            await this.$http.post(
                `/api/v1/roles/${this.localValue.id}/permissions/attach`,
                { permission_id: permission.id }
            );
            hasChanges = true;

            const hasChildren = this.allData.some(p => p.parent?.id === permission.id);
            if (hasChildren) {
              await this.refreshPermissionsForParent(permission.id);
            }
          } catch (error) {
            console.error(`Yetki eklenirken hata (${permissionName}):`, error);
            this.StatusCodeHelper.showMessage(error);
            this.localValue.permissions = this.localValue.permissions.filter(
                (p: string) => p !== permissionName
            );
          }
        }
      }

      // Çıkarılan her yetki için detach isteği at
      for (const permissionName of removedPermissions) {
        const permission = this.allData.find(p => p.name === permissionName);
        if (permission) {
          try {
            await this.$http.post(
                `/api/v1/roles/${this.localValue.id}/permissions/detach`,
                { permission_id: permission.id }
            );
            hasChanges = true;

            const hasChildren = this.allData.some(p => p.parent?.id === permission.id);
            if (hasChildren) {
              await this.refreshPermissionsForParent(permission.id);
            }
          } catch (error) {
            console.error(`Yetki kaldırılırken hata (${permissionName}):`, error);
            this.StatusCodeHelper.showMessage(error);
            if (!this.localValue.permissions.includes(permissionName)) {
              this.localValue.permissions.push(permissionName);
            }
          }
        }
      }


      if (hasChanges) {
        this.previousPermissions = [...this.localValue.permissions];

        this.$emit('load');

        this.$toast.dismiss('permission-update');
        this.$toast.success('İzinler başarıyla güncellendi');
      } else {
        this.$toast.dismiss('permission-update');
      }
    } catch (error) {
      console.error("Permission değişikliği sırasında hata:", error);
    } finally {
      this.isProcessing = false;
    }
  }

  async updateRoleName() {
    if (this.localValue.id && this.localValue.name) {
      try {
        await this.$http.patch(`/api/v1/roles/${this.localValue.id}`, {
          name: this.localValue.name
        });
        this.$emit('load');
      } catch (error) {
        console.error("Role adı güncellenirken hata:", error);
        this.StatusCodeHelper.showMessage(error);
      }
    }
  }
}
